"use client";

import * as React from "react";
import { XIcon } from "lucide-react";
import { useIsomorphicLayoutEffect } from "usehooks-ts";

import { cn, debounce } from "../../lib/utils";
import { Badge, type BadgeColor } from "./badge";
import {
	SelectCoreProvider,
	SelectCoreTrigger,
	SelectCoreContent,
	SelectCoreItem,
	SelectCoreGroup,
	SelectCoreSeparator,
	useSelectCoreContext,
	useControllableState,
	makeCollectItemLabels,
	type SelectCoreTriggerProps,
	type SelectCoreContentProps,
	type SelectCoreItemProps,
	type SelectedItem,
} from "./combobox-base";

/** Props for the {@link MultiSelect} root component. */
export type MultiSelectProps = {
	children: React.ReactNode;
	/** Controlled selected values. When provided the component is fully controlled. */
	values?: string[];
	/** Initial selected values for uncontrolled usage. */
	defaultValues?: string[];
	/** Callback fired when the set of selected values changes. */
	onValuesChange?: (values: string[]) => void;
	/** Callback fired when the popover open state changes (e.g. to save on close). */
	onOpenChange?: (open: boolean) => void;
};

/**
 * Composable multi-value selector built on {@link Popover} and {@link Command}.
 *
 * Supports both controlled (`values` + `onValuesChange`) and uncontrolled
 * (`defaultValues`) modes.
 */
function MultiSelect(props: MultiSelectProps) {
	const { children, values, defaultValues, onValuesChange, onOpenChange } =
		props;
	// Controlled when the `values` prop is present, even if it is `undefined`
	// (e.g. a controlled-but-initially-empty field). Keying off presence rather
	// than the value keeps controlled-ness stable across clears.
	const isControlled = "values" in props;

	const [open, setOpenInternal] = React.useState(false);
	const setOpen = React.useCallback(
		(nextOpen: boolean) => {
			setOpenInternal(nextOpen);
			onOpenChange?.(nextOpen);
		},
		[onOpenChange],
	);

	const [selectedArray, setSelectedValues] = useControllableState<string[]>(
		isControlled,
		values,
		defaultValues,
	);
	const selectedSet = new Set(selectedArray ?? []);

	function toggle(value: string) {
		const newSet = new Set(selectedSet);
		if (newSet.has(value)) {
			newSet.delete(value);
		} else {
			newSet.add(value);
		}
		const next = Array.from(newSet);
		setSelectedValues(next);
		onValuesChange?.(next);
	}

	return (
		<SelectCoreProvider
			open={open}
			setOpen={setOpen}
			multiple={true}
			selectedValues={Array.from(selectedSet)}
			isSelected={(value) => selectedSet.has(value)}
			toggle={toggle}
			collectItemLabels={collectMultiSelectItemLabels}
		>
			{children}
		</SelectCoreProvider>
	);
}
MultiSelect.displayName = "MultiSelect";

/** Props for the {@link MultiSelectTrigger} component. */
export type MultiSelectTriggerProps = SelectCoreTriggerProps;

/** Trigger button that opens the multi-select popover. Renders as a combobox. */
const MultiSelectTrigger = SelectCoreTrigger;

/** Props for the {@link MultiSelectValue} component. */
export type MultiSelectValueProps = {
	/** Text shown when nothing is selected. */
	placeholder?: string;
	/** Whether clicking a badge removes the value. Defaults to `true`. */
	clickToRemove?: boolean;
	/**
	 * How the value container handles horizontal overflow.
	 *
	 * - `"wrap"` – always wraps badges to new lines.
	 * - `"wrap-when-open"` – wraps only while the popover is open (default).
	 * - `"cutoff"` – hides overflowing badges and shows a "+N" indicator.
	 */
	overflowBehavior?: "wrap" | "wrap-when-open" | "cutoff";
	/**
	 * Render a custom display for the selected values (e.g. a summary string
	 * such as "All" / "3 selected"). Takes precedence over the default badge
	 * pills and owns rendering for all states, including when nothing is
	 * selected. Receives the ordered selected items with their registered labels.
	 */
	format?: (selected: SelectedItem[]) => React.ReactNode;
	/** Colour variant for the selected-value badges. Defaults to "indigo". */
	badgeVariant?: BadgeColor;
} & Omit<React.ComponentPropsWithoutRef<"div">, "children">;

/** Renders the currently selected values as badges with optional overflow handling. */
function MultiSelectValue({
	placeholder,
	clickToRemove = true,
	className,
	overflowBehavior = "wrap-when-open",
	format,
	badgeVariant = "indigo",
	...props
}: MultiSelectValueProps) {
	const { selectedValues, toggle, items, open } = useSelectCoreContext();
	const [overflowAmount, setOverflowAmount] = React.useState(0);
	const valueRef = React.useRef<HTMLDivElement>(null);
	const overflowRef = React.useRef<HTMLDivElement>(null);

	const shouldWrap =
		overflowBehavior === "wrap" ||
		(overflowBehavior === "wrap-when-open" && open);
	// Read inside `checkOverflow` through a ref so the callback identity stays
	// stable and the observers below are attached once per mount. Published in a
	// layout effect rather than during render: the mutation observer reads it
	// from a microtask that drains before passive effects, so a passive write
	// would leave it a commit behind on the wrap/unwrap transition.
	const shouldWrapRef = React.useRef(shouldWrap);
	useIsomorphicLayoutEffect(() => {
		shouldWrapRef.current = shouldWrap;
	}, [shouldWrap]);

	const checkOverflow = React.useCallback(() => {
		if (valueRef.current == null) {
			return;
		}

		const containerElement = valueRef.current;
		const overflowElement = overflowRef.current;
		const selectedItemElements = containerElement.querySelectorAll<HTMLElement>(
			"[data-selected-item]",
		);

		if (overflowElement != null) {
			overflowElement.style.display = "none";
		}
		selectedItemElements.forEach((child) =>
			child.style.removeProperty("display"),
		);

		// A wrapping container gives every badge its own line and keeps the "+N"
		// indicator hidden, so there is nothing to hide. Hiding anyway fed the
		// trigger's content-derived width (`w-fit`) back into the measurement
		// below: each pass changed the layout, the observers re-ran it, and the
		// cycle never settled.
		if (shouldWrapRef.current) {
			setOverflowAmount(0);
			return;
		}

		let amount = 0;
		for (let i = selectedItemElements.length - 1; i >= 0; i--) {
			const child = selectedItemElements[i]!;
			if (containerElement.scrollWidth <= containerElement.clientWidth) {
				break;
			}
			amount = selectedItemElements.length - i;
			child.style.display = "none";
			overflowElement?.style.removeProperty("display");
		}
		setOverflowAmount(amount);
	}, []);

	const handleResize = React.useCallback(
		(node: HTMLDivElement) => {
			valueRef.current = node;

			const mutationObserver = new MutationObserver(checkOverflow);
			const observer = new ResizeObserver(debounce(checkOverflow, 100));

			mutationObserver.observe(node, {
				childList: true,
				attributes: true,
				attributeFilter: ["class", "style"],
			});
			observer.observe(node);

			return () => {
				observer.disconnect();
				mutationObserver.disconnect();
				valueRef.current = null;
			};
		},
		[checkOverflow],
	);

	if (format) {
		return (
			<span
				{...(props as React.HTMLAttributes<HTMLSpanElement>)}
				className={cn("min-w-0 overflow-hidden truncate", className)}
			>
				{format(
					selectedValues.map((value) => ({
						value,
						label: items.get(value) ?? value,
					})),
				)}
			</span>
		);
	}

	if (selectedValues.length === 0 && placeholder) {
		return (
			<span className="min-w-0 overflow-hidden font-normal text-neutral-500 dark:text-neutral-400">
				{placeholder}
			</span>
		);
	}

	return (
		<div
			{...props}
			ref={handleResize}
			className={cn(
				"flex w-full gap-1.5 overflow-hidden",
				shouldWrap && "h-full flex-wrap",
				className,
			)}
		>
			{selectedValues.map((value) => {
				const label = items.get(value) ?? value;
				return (
					<Badge
						variant={badgeVariant}
						shape="label"
						size="sm"
						data-selected-item
						data-testid="selected-item"
						className="group flex w-auto shrink-0 items-center gap-1"
						key={value}
						onClick={
							clickToRemove
								? (e) => {
										e.stopPropagation();
										toggle(value);
									}
								: undefined
						}
					>
						{label}
						{clickToRemove && <XIcon className="size-2" />}
					</Badge>
				);
			})}
			<Badge
				style={{
					display: overflowAmount > 0 && !shouldWrap ? "block" : "none",
				}}
				className="shrink-0"
				variant={badgeVariant}
				shape="label"
				size="sm"
				ref={overflowRef}
			>
				+{overflowAmount}
			</Badge>
		</div>
	);
}
MultiSelectValue.displayName = "MultiSelectValue";

/** Props for the {@link MultiSelectContent} component. */
export type MultiSelectContentProps = SelectCoreContentProps;

/** Popover content containing a searchable list of options. */
const MultiSelectContent = SelectCoreContent;

/** Props for the {@link MultiSelectItem} component. */
export type MultiSelectItemProps = {
	/** The string value that identifies this option. */
	value: string;
	/** Override the label shown in the selected-value badge. Falls back to `children`. */
	badgeLabel?: React.ReactNode;
	/** Muted subtext rendered beneath the option label. Never shown in the selected-value badge. */
	description?: React.ReactNode;
} & Omit<SelectCoreItemProps, "value" | "label">;

/** A single selectable option within {@link MultiSelectContent}. */
function MultiSelectItem({ badgeLabel, ...props }: MultiSelectItemProps) {
	return <SelectCoreItem label={badgeLabel} {...props} />;
}
MultiSelectItem.displayName = "MultiSelectItem";

const collectMultiSelectItemLabels = makeCollectItemLabels(
	MultiSelectItem,
	(itemProps) => itemProps.badgeLabel as React.ReactNode,
);

/** Visually groups related {@link MultiSelectItem}s under an optional heading. */
const MultiSelectGroup = SelectCoreGroup;

/** Horizontal rule between groups of {@link MultiSelectItem}s. */
const MultiSelectSeparator = SelectCoreSeparator;

export {
	MultiSelect,
	MultiSelectTrigger,
	MultiSelectValue,
	MultiSelectContent,
	MultiSelectItem,
	MultiSelectGroup,
	MultiSelectSeparator,
};
